import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { DiscountCodeZ as DiscountCode } from '@libs/schemas';
import { DiscountCodeFormField } from 'pages/Discounts/formFields/DiscountCodeFormField';
import { PricingRuleFormField } from 'pages/Discounts/formFields/PricingRuleFormField';
import { UsageSettingsFormField } from 'pages/Discounts/formFields/UsageSettingsFormField';
import { customZodResolver } from 'pages/Discounts/CreateDiscountCode/utilities/customZodResolver';
import { discountCodeSchema } from '@libs/schemas';
import { trpc } from 'src/trpc';
import { useToast } from 'components/useToast';
import { MAX_DATE } from '@libs/schemas';
import { forwardRef, useImperativeHandle } from 'react';
import { SentryMonitoringService } from '@libs/frontend-services';

export interface DiscountCodeFormRef {
  submit: () => void;
  isSubmitting: boolean;
  isDisabled: boolean;
}

interface EditDiscountCodeFormProps {
  onCancel: () => void;
  code?: DiscountCode | null;
}

const discountCodeResolver = customZodResolver(discountCodeSchema);

export const DiscountCodeForm = forwardRef<DiscountCodeFormRef, EditDiscountCodeFormProps>(
  ({ onCancel, code }, ref) => {
    const sentryMonitoringService = new SentryMonitoringService();
    const isEditing = Boolean(code);
    const form = useForm<DiscountCode>({
      resolver: discountCodeResolver,
      defaultValues: {
        code: code?.code || '',
        ruleId: code?.ruleId || '',
        usageLimit: code?.usageLimit || null,
        active: true,
        expiresAt: code?.expiresAt
          ? new Date(code.expiresAt).toISOString()
          : new Date(MAX_DATE).toISOString(),
      },
      mode: 'onSubmit',
      reValidateMode: 'onChange',
    });
    const { successToast, errorToast } = useToast();
    const utils = trpc.useUtils();
    const { mutate: createDiscountCode, isPending: isCreating } =
      trpc.admin.discountCodes.createDiscount.useMutation({
        onSuccess: () => {
          successToast({
            title: 'Discount code created successfully',
          });
          utils.admin.discountCodes.getAllDiscountCodes.invalidate();
          onCancel();
        },
        onError: (error) => {
          if (error.data?.code === 'CONFLICT') {
            errorToast({
              title: 'Discount Code Already Exists',
              description: 'This discount code already exists. Please choose a different code.',
            });
          } else {
            sentryMonitoringService.captureException(error as unknown as Error);
            errorToast({
              title: 'Error',
              description: 'Failed to create discount code. Please try again.',
            });
          }
        },
      });
    const { mutate: updateDiscountCode, isPending: isUpdating } =
      trpc.admin.discountCodes.updateDiscount.useMutation({
        onSuccess: () => {
          successToast({
            title: 'Discount code updated successfully',
          });
          utils.admin.discountCodes.getAllDiscountCodes.invalidate();
          onCancel();
        },
        onError: (error) => {
          if (error.data?.code === 'CONFLICT') {
            errorToast({
              title: 'Discount Code Already Exists',
              description: 'This discount code already exists. Please choose a different code.',
            });
          } else {
            errorToast({
              title: 'Error',
              description: error.message || 'Failed to update discount code. Please try again.',
            });
          }
        },
      });

    const onSubmit = (data: DiscountCode) => {
      if (isEditing) {
        updateDiscountCode({
          ...data,
          _id: code?._id as string,
          expiresAt: data.expiresAt,
          archived: data.archived,
        });
      } else {
        createDiscountCode({
          ...data,
          expiresAt: data.expiresAt,
          archived: data.archived,
        });
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => form.handleSubmit(onSubmit)(),
      isSubmitting: isCreating || isUpdating,
      isDisabled: (isEditing && !code?._id) || code?.archived || false,
    }));

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <DiscountCodeFormField />
          <PricingRuleFormField />
          <UsageSettingsFormField />
        </form>
      </Form>
    );
  },
);
